import { css } from "@compiled/react";

import StrokeWeightExtraLargeIcon from "@atlaskit/icon/core/stroke-weight-extra-large";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

export interface NavLinkProps {
    text: string;
    href: string;
    depth: number;
    isActive: boolean;
}

export interface SideNavLinkProps {
    type: "link";
    text: string;
    href: string;
    depth?: number;
    expandParentCollection?: () => void;
}

const getCurrRoute = () => {
    let currRoute = window.location.pathname;
    if (currRoute === "/uniform-preview") {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        currRoute = urlParams.get("path") ?? "";
    }
    return currRoute;
};

const NavLink = ({ text, href, depth, isActive }: NavLinkProps) => {
    return (
        <a
            href={href}
            css={[
                navLinkCss,
                depth > 0 && subLevelLinkCss,
                !isActive && navLinkHoverCss
            ]}
        >
            <div css={dotContainerCss}>
                {isActive && <StrokeWeightExtraLargeIcon label="" />}
            </div>
            <TypographyStyles>
                <span
                    className={depth === 0 ? "h6" : "p"}
                    css={[isActive && css({ fontWeight: "700" })]}
                >
                    {text}
                </span>
            </TypographyStyles>
        </a>
    );
};

export const SideNavLink = ({
    text,
    href,
    depth = 0,
    expandParentCollection
}: SideNavLinkProps) => {
    const localizedHref = useLocalizedHref(href);

    const currRoute = getCurrRoute();
    const isActive = currRoute === localizedHref;

    if (expandParentCollection && isActive) {
        expandParentCollection();
    }

    return (
        <li
            css={[
                listStyleNoneCss,
                depth === 0 && css({ paddingBlock: token("space.200") })
            ]}
        >
            <NavLink
                href={localizedHref}
                text={text}
                depth={depth}
                isActive={isActive}
            />
        </li>
    );
};

const listStyleNoneCss = css({
    listStyle: "none",
    margin: token("space.0"),
    padding: token("space.0")
});

const navLinkCss = css({
    display: "flex",
    alignItems: "center"
});

const navLinkHoverCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover > div > span": {
        color: wacToken("color.primary.blue")
    }
});

const subLevelLinkCss = css({
    paddingBlock: token("space.100")
});

const dotContainerCss = css({
    width: "24px",
    height: "24px",
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    marginInlineEnd: token("space.050")
});
